import React, { useEffect, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase } from "../theme";

export const PAYMENT_METHODS = ["Check", "Cash", "Bank transfer", "Zelle", "Card", "Other"];

const CAP_LABELS = {
  max_categories: "Max categories",
  max_items_per_category: "Max items per category",
  max_tables: "Max tables",
  report_retention_days: "Report retention (days)",
  split_bill_enabled: "Split bill",
};

export const money = (n) => `$${Number(n).toFixed(2)}`;

// Dates from the API are UTC. A plain 'YYYY-MM-DD' is a calendar date and is
// shown as-is (no timezone shift); 'YYYY-MM-DD HH:MM:SS' is a UTC timestamp,
// shown as the viewer's local date.
export function fmtDate(value) {
  if (!value) return "Not set";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  const d = new Date(`${s.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString();
}

// The one-line payment status shown under a tenant's name. It walks through
// invoice sent -> customer agreed -> paid, and is replaced by the next
// invoice's "sent" when that goes out.
export function paymentNote(fee) {
  const what = fee.type === "invoice" ? "Invoice" : "Fee request";
  switch (fee.state) {
    case "paid":
      return { text: `Paid ${money(fee.amount)} on ${fmtDate(fee.paid_at)}`, color: colors.statusReady };
    case "activated":
      return { text: `Access on grace until ${fmtDate(fee.grace_until)}, payment pending (${money(fee.amount)})`, color: colors.statusProgress };
    case "agreed":
      return { text: `Agreed ${money(fee.amount)} on ${fmtDate(fee.agreed_at)}, awaiting payment`, color: colors.statusProgress };
    default:
      return { text: `${what} sent ${fmtDate(fee.sent_at)} (${money(fee.amount)}), awaiting agreement`, color: colors.textMuted };
  }
}

export function PaymentNote({ fee }) {
  const n = paymentNote(fee);
  return <div style={{ fontSize: 11, color: n.color, marginTop: 2, fontWeight: 600 }}>{n.text}</div>;
}

// Review an invoice / fee and record what happened to it: mark it paid
// (renewing the subscription) or give the tenant grace access until payment.
export default function PaymentModal({ invoiceId, initialTab, authHeader, onClose, onChanged }) {
  const [inv, setInv] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState(initialTab === "grace" ? "grace" : "pay");
  const [paidDate, setPaidDate] = useState("");
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [expiration, setExpiration] = useState("");
  const [sendReceipt, setSendReceipt] = useState(true);
  const [graceDays, setGraceDays] = useState("14");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const sessionExpired = (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem("admin_token");
      window.location.href = "/admin/login";
      return true;
    }
    return false;
  };

  useEffect(() => {
    axios.get(endpoints.adminInvoice(invoiceId), { headers: authHeader() })
      .then((res) => {
        const i = res.data.invoice;
        setInv(i);
        setPaidDate(i.suggested_paid_date || "");
        setExpiration(i.suggested_expiration || "");
      })
      .catch((err) => {
        if (sessionExpired(err)) return;
        setLoadError(err.response?.data?.error || "Could not load this invoice.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const isInvoice = inv?.type === "invoice";

  const markPaid = async () => {
    setError("");
    if (!paidDate) { setError("Enter the payment date."); return; }
    if (isInvoice && expiration && expiration < paidDate) {
      setError("The new expiration cannot be before the payment date.");
      return;
    }
    setBusy(true);
    try {
      const body = { agreement_id: inv.id, paid_date: paidDate, method, reference: reference.trim(), send_receipt: sendReceipt };
      if (isInvoice && expiration) body.new_expiration_date = expiration;
      const res = await axios.post(endpoints.adminInvoiceMarkPaid, body, { headers: authHeader() });
      setResult({
        message: res.data.message,
        receipt: sendReceipt ? (res.data.receipt_sent ? "Receipt emailed to the tenant." : "The receipt email could not be sent. The payment is still recorded.") : "No receipt sent.",
      });
      onChanged();
    } catch (err) {
      if (!sessionExpired(err)) setError(err.response?.data?.error || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const activateGrace = async () => {
    setError("");
    const days = parseInt(graceDays, 10);
    if (!(days >= 1 && days <= 365)) { setError("Enter a grace period between 1 and 365 days."); return; }
    setBusy(true);
    try {
      const res = await axios.post(endpoints.adminInvoiceActivateGrace, { agreement_id: inv.id, grace_days: days }, { headers: authHeader() });
      setResult({ message: res.data.message, receipt: "" });
      onChanged();
    } catch (err) {
      if (!sessionExpired(err)) setError(err.response?.data?.error || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const fee = inv && {
    type: inv.type, state: inv.state, amount: inv.fee_amount, agreed_at: inv.agreed_at,
    sent_at: inv.created_at, paid_at: inv.paid_at, grace_until: inv.grace_until,
  };
  const unpaid = inv && (inv.state === "sent" || inv.state === "agreed" || inv.state === "activated");
  const canGrace = isInvoice && (inv.state === "sent" || inv.state === "agreed");

  return (
    <div style={styles.overlay}>
      <div style={styles.modal} role="dialog" aria-label="Review payment">
        <h2 style={{ fontFamily: fonts.display, fontSize: 18, margin: `0 0 ${space[3]}` }}>Review payment</h2>

        {loadError && <p style={styles.error}>{loadError}</p>}
        {!inv && !loadError && <p style={{ color: colors.textSecondary }}>Loading…</p>}

        {inv && result && (
          <>
            <div style={styles.ok}>{result.message}</div>
            {result.receipt && <p style={styles.hint}>{result.receipt}</p>}
          </>
        )}

        {inv && !result && (
          <>
            <div style={styles.details}>
              <Row label="Tenant" value={inv.business_name} />
              <Row label="Tenant email" value={inv.tenant_email} />
              <Row label="Type" value={isInvoice ? "Subscription invoice" : "Cap-increase fee"} />
              <Row label="Amount" value={money(inv.fee_amount)} />
              <Row label="Status" value={paymentNote(fee).text} />
              {!isInvoice && inv.cap_field_name && (
                <Row label="Requested change" value={`${CAP_LABELS[inv.cap_field_name] || inv.cap_field_name} → ${inv.cap_requested_value}`} />
              )}
              {isInvoice && <Row label="Subscription expires" value={fmtDate(inv.subscription_expiration_date)} />}
              {isInvoice && <Row label="Access ends" value={fmtDate(inv.trial_end)} />}
            </div>

            {inv.state === "paid" && (
              <div style={styles.ok}>
                Already marked paid on {fmtDate(inv.paid_at)}
                {[inv.paid_method, inv.paid_reference].filter(Boolean).length > 0 && ` (${[inv.paid_method, inv.paid_reference].filter(Boolean).join(" · ")})`}.
                Nothing more to do.
              </div>
            )}

            {inv.state === "replaced" && (
              <div style={styles.warn}>
                This invoice was replaced by a newer one (or covered by another payment), so it can’t be marked paid.
                Use the current invoice instead.
              </div>
            )}

            {unpaid && canGrace && (
              <div style={{ display: "flex", gap: space[2], margin: `${space[3]} 0` }}>
                <button onClick={() => { setTab("pay"); setError(""); }} style={tab === "pay" ? styles.tabOn : styles.tabOff}>Mark as Paid</button>
                <button onClick={() => { setTab("grace"); setError(""); }} style={tab === "grace" ? styles.tabOn : styles.tabOff}>Activate now (payment pending)</button>
              </div>
            )}

            {unpaid && (tab === "pay" || !canGrace) && (
              <div>
                {isInvoice && inv.open_siblings > 0 && (
                  <div style={styles.warn}>
                    Marking this paid will also close {inv.open_siblings} other unpaid invoice(s) for the same renewal, so it can’t be paid twice.
                  </div>
                )}
                {!isInvoice && (
                  <div style={styles.warn}>
                    This only records the payment. Approve the pending limit request separately under Pending Limit Requests.
                  </div>
                )}
                <Field label="Payment date">
                  <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} style={styles.input} />
                </Field>
                <Field label="Method">
                  <select value={method} onChange={(e) => setMethod(e.target.value)} style={styles.input}>
                    <option value="">Select…</option>
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Reference (optional)">
                  <input type="text" maxLength={120} placeholder="Check #, transaction ID…" value={reference} onChange={(e) => setReference(e.target.value)} style={styles.input} />
                </Field>
                {isInvoice && (
                  <Field label="New subscription expiration" hint="One month past the current expiration by default. Change it for a longer period.">
                    <input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} style={styles.input} />
                  </Field>
                )}
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: space[3] }}>
                  <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} style={{ marginTop: 3 }} />
                  Email a payment receipt to {inv.tenant_email}
                </label>
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.actions}>
                  <button onClick={markPaid} disabled={busy} style={{ ...buttonPrimary, opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Saving…" : "Confirm and Mark as Paid"}
                  </button>
                </div>
              </div>
            )}

            {unpaid && canGrace && tab === "grace" && (
              <div>
                <p style={styles.hint}>
                  Gives the tenant access for a grace period while payment is still pending. The subscription stays unpaid until you mark it paid;
                  if it is still unpaid when the grace period ends, access turns off automatically.
                </p>
                <Field label="Grace period (days)">
                  <input type="number" min="1" max="365" value={graceDays} onChange={(e) => setGraceDays(e.target.value)} style={styles.input} />
                </Field>
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.actions}>
                  <button onClick={activateGrace} disabled={busy} style={{ ...buttonPrimary, opacity: busy ? 0.6 : 1 }}>
                    {busy ? "Saving…" : "Activate now"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ ...styles.actions, marginTop: space[4] }}>
          <button onClick={onClose} style={buttonSecondary}>{result ? "Close" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", gap: space[3], padding: "3px 0", fontSize: 13 }}>
      <span style={{ width: 130, color: colors.textSecondary, flexShrink: 0 }}>{label}</span>
      <span style={{ wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginTop: space[3] }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={styles.hint}>{hint}</div>}
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { width: 480, maxWidth: "94vw", maxHeight: "92vh", overflowY: "auto", background: colors.surface, borderRadius: radius.md, padding: space[5], fontFamily: fonts.body, color: colors.textPrimary },
  details: { background: colors.surfaceMuted, borderRadius: radius.sm, padding: space[3] },
  input: { ...inputBase, width: "100%", boxSizing: "border-box" },
  hint: { fontSize: 12, color: colors.textMuted, marginTop: 4 },
  error: { color: "#b91c1c", fontSize: 13, margin: `${space[2]} 0 0` },
  ok: { background: "#ecfdf3", color: "#067647", borderRadius: radius.sm, padding: space[3], fontSize: 13, marginTop: space[3] },
  warn: { background: "#fffaeb", color: "#93370d", borderRadius: radius.sm, padding: space[3], fontSize: 13, marginTop: space[3] },
  actions: { display: "flex", gap: space[2], marginTop: space[4] },
  tabOn: { padding: "6px 12px", fontSize: 12, borderRadius: radius.sm, border: `1px solid ${colors.brandBlue}`, background: colors.brandBlue, color: "#fff", cursor: "pointer" },
  tabOff: { padding: "6px 12px", fontSize: 12, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer" },
};
